// HowItWorksModal.tsx
// Educational modal explaining the mathematical and visual concepts behind palette transformation

import React, { useState } from 'react';

interface HowItWorksModalProps {
  onClose: () => void;
}

export const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "1. Color as a Point",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            Every color can be represented as a point in 3D space with three coordinates.
          </p>
          
          <div className="bg-gray-900 p-3 rounded flex justify-center">
            <div className="relative w-40 h-40">
              {/* 3D axes visualization */}
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Axes */}
                <line x1="100" y1="100" x2="170" y2="100" stroke="#ef4444" strokeWidth="2" />
                <line x1="100" y1="100" x2="100" y2="30" stroke="#10b981" strokeWidth="2" />
                <line x1="100" y1="100" x2="50" y2="150" stroke="#3b82f6" strokeWidth="2" />
                
                {/* Labels */}
                <text x="175" y="105" fill="#ef4444" fontSize="14">R</text>
                <text x="105" y="25" fill="#10b981" fontSize="14">G</text>
                <text x="40" y="155" fill="#3b82f6" fontSize="14">B</text>
                
                {/* Example point */}
                <circle cx="140" cy="70" r="5" fill="#ec4899" />
              </svg>
            </div>
          </div>
          
          <div className="text-xs text-gray-400 bg-gray-900 p-2 rounded font-mono text-center">
            #2563eb = <span className="text-blue-400">RGB(37, 99, 235)</span>
          </div>
        </div>
      )
    },
    {
      title: "2. The RGB Problem",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            RGB space is <strong className="text-red-400">not perceptually uniform</strong>. 
            Equal distances don't mean equal visual differences.
          </p>
          
          <div className="bg-gray-900 p-3 rounded space-y-2">
            <div>
              <div className="text-xs text-gray-400 mb-1">+1 change in dark (barely visible):</div>
              <div className="flex gap-2 items-center">
                <div className="w-12 h-12 rounded border border-gray-600" style={{backgroundColor: '#640000'}}></div>
                <div className="text-lg">→</div>
                <div className="w-12 h-12 rounded border border-gray-600" style={{backgroundColor: '#650000'}}></div>
              </div>
            </div>
            
            <div>
              <div className="text-xs text-gray-400 mb-1">Similar change in mid-tones (very visible):</div>
              <div className="flex gap-2 items-center">
                <div className="w-12 h-12 rounded border border-gray-600" style={{backgroundColor: '#7f7f7f'}}></div>
                <div className="text-lg">→</div>
                <div className="w-12 h-12 rounded border border-gray-600" style={{backgroundColor: '#8c8c8c'}}></div>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-yellow-400 bg-yellow-900 bg-opacity-20 p-2 rounded">
            ⚠️ This makes color manipulation unpredictable
          </p>
        </div>
      )
    },
    {
      title: "3. LAB Color Space",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            <strong className="text-green-400">LAB space is perceptually uniform</strong>. 
            Equal distances = equal perceived differences.
          </p>
          
          <div className="bg-gray-900 p-3 rounded">
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 font-bold text-blue-400">L*</div>
                <div className="flex-1 text-xs">Lightness: 0 (black) → 100 (white)</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 font-bold text-green-400">a*</div>
                <div className="flex-1 text-xs">Green (-) ↔ Red (+)</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 font-bold text-yellow-400">b*</div>
                <div className="flex-1 text-xs">Blue (-) ↔ Yellow (+)</div>
              </div>
            </div>
          </div>
          
          <div className="text-xs text-gray-400 bg-gray-900 p-2 rounded text-center">
            Pipeline: <span className="font-mono text-blue-400">Hex → RGB → XYZ → LAB</span>
          </div>
        </div>
      )
    },
    {
      title: "4. Mapping to Sphere",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            We map LAB coordinates to points on a unit sphere for rotation.
          </p>
          
          <div className="bg-gray-900 p-3 rounded flex justify-center">
            <svg viewBox="0 0 160 160" className="w-32 h-32">
              {/* Sphere */}
              <circle cx="80" cy="80" r="50" fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="3,3" />
              
              {/* Reference point */}
              <circle cx="80" cy="30" r="4" fill="#ec4899" />
              <text x="88" y="28" fill="#ec4899" fontSize="10">(0,0,1)</text>
              
              {/* Color points */}
              <circle cx="110" cy="65" r="2.5" fill="#ef4444" />
              <circle cx="60" cy="90" r="2.5" fill="#10b981" />
              <circle cx="95" cy="105" r="2.5" fill="#f59e0b" />
              
              {/* Center */}
              <circle cx="80" cy="80" r="2" fill="#fff" />
            </svg>
          </div>
          
          <div className="text-xs text-gray-400 bg-gray-900 p-2 rounded font-mono text-center">
            (L/100, a/128, b/128)
          </div>
        </div>
      )
    },
    {
      title: "5. Rodrigues' Rotation",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            When you drag, we rotate all colors using <strong className="text-purple-400">Rodrigues' formula</strong>.
          </p>
          
          <div className="bg-gray-900 p-2 rounded text-xs font-mono text-gray-300 overflow-x-auto">
            <div className="text-purple-400 text-center mb-1">v' = v·cos(θ) + (k×v)·sin(θ) + k·(k·v)·(1-cos(θ))</div>
          </div>
          
          <div className="bg-gray-900 p-3 rounded flex justify-center">
            <svg viewBox="0 0 180 80" className="w-full h-20">
              {/* Before */}
              <circle cx="40" cy="40" r="25" fill="none" stroke="#3b82f6" strokeWidth="2" />
              <circle cx="40" cy="15" r="3" fill="#ec4899" />
              
              {/* Arrow */}
              <path d="M 70 40 L 110 40" stroke="#fff" strokeWidth="2" markerEnd="url(#arrow)" />
              <defs>
                <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#fff" />
                </marker>
              </defs>
              
              {/* After */}
              <circle cx="140" cy="40" r="25" fill="none" stroke="#3b82f6" strokeWidth="2" />
              <circle cx="165" cy="40" r="3" fill="#ec4899" />
              
              <text x="40" y="75" fill="#888" fontSize="9" textAnchor="middle">Before</text>
              <text x="140" y="75" fill="#888" fontSize="9" textAnchor="middle">After</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "6. Back to Colors",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            After rotation, we transform back to displayable colors.
          </p>
          
          <div className="bg-gray-900 p-3 rounded">
            <div className="text-xs space-y-1">
              <div>1. Rotate in LAB space</div>
              <div>2. LAB → XYZ</div>
              <div>3. XYZ → RGB</div>
              <div>4. Apply gamma correction</div>
              <div>5. RGB → Hex</div>
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-3 bg-gray-900 p-2 rounded">
            <div className="text-center">
              <div className="w-14 h-14 rounded mx-auto mb-1" style={{backgroundColor: '#2563eb'}}></div>
              <div className="text-xs font-mono">#2563eb</div>
            </div>
            <div className="text-xl">→</div>
            <div className="text-center">
              <div className="w-14 h-14 rounded mx-auto mb-1" style={{backgroundColor: '#eb6425'}}></div>
              <div className="text-xs font-mono">#eb6425</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "7. Why It Works",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-gray-300">
            LAB space + spherical rotation = harmonious transformations.
          </p>
          
          <div className="space-y-2">
            <div className="bg-green-900 bg-opacity-20 border border-green-700 p-2 rounded">
              <div className="text-green-400 font-semibold text-sm">✓ Perceptual Uniformity</div>
              <div className="text-xs text-gray-300">Similar colors stay similar</div>
            </div>
            
            <div className="bg-blue-900 bg-opacity-20 border border-blue-700 p-2 rounded">
              <div className="text-blue-400 font-semibold text-sm">✓ Distance Preservation</div>
              <div className="text-xs text-gray-300">Maintains relationships</div>
            </div>
            
            <div className="bg-purple-900 bg-opacity-20 border border-purple-700 p-2 rounded">
              <div className="text-purple-400 font-semibold text-sm">✓ Continuous Exploration</div>
              <div className="text-xs text-gray-300">Smooth transitions</div>
            </div>
          </div>
          
          <div className="bg-gray-900 p-2 rounded text-xs text-gray-400 text-center">
            180° rotation = <strong className="text-yellow-400">perceptually opposite</strong> palette
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-xl border border-gray-700 shadow-2xl flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-bold">How It Works</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>

        {/* Content - Fixed height, no scrolling */}
        <div className="p-4 flex-shrink-0" style={{ minHeight: '320px' }}>
          <h3 className="text-lg font-semibold mb-3 text-blue-400">{steps[currentStep].title}</h3>
          <div>{steps[currentStep].content}</div>
        </div>

        {/* Navigation */}
        <div className="border-t border-gray-700 p-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
            >
              ← Previous
            </button>
            
            <div className="text-sm text-gray-400">
              {currentStep + 1} / {steps.length}
            </div>
            
            <button
              onClick={() => {
                if (currentStep === steps.length - 1) {
                  onClose();
                } else {
                  setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
                }
              }}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Close' : 'Next →'}
            </button>
          </div>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-1.5">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  index === currentStep ? 'bg-blue-500' : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};